import type { PromptDocument, Tag } from '../types'
import type { PlatformRenderer } from './index'

/**
 * Stable Diffusion rendering rules:
 *   - Positive prompt: all enabled sections joined by `, `.
 *   - Tags with `weight !== 1` become `(tag:weight)`.
 *   - MJ's `--no` becomes the "Negative prompt:" line (SD Web UI convention).
 *   - MJ's `--ar` is translated to a trailing hint `(aspect ratio: w:h)` so the
 *     information isn't lost, since SD doesn't consume it natively.
 *   - Other MJ params (--v/--style/--q/--s/--chaos) are dropped; they have no
 *     1:1 SD mapping.
 */
function renderTag(t: Tag): string {
  const raw = t.raw.trim()
  if (!raw)
    return ''
  if (t.weight && t.weight !== 1)
    return `(${raw}:${Number(t.weight.toFixed(2))})`
  return raw
}

export const stableDiffusionRenderer: PlatformRenderer = {
  id: 'stable-diffusion',
  label: 'Stable Diffusion',
  render(doc) {
    const positive = doc.sections
      .filter(s => s.enabled)
      .slice()
      .sort((a, b) => a.order - b.order)
      .flatMap(s => s.tags.map(renderTag).filter(Boolean))
      .join(', ')

    const lines: string[] = []
    if (positive)
      lines.push(positive)

    const negatives = doc.params.no?.map(n => n.trim()).filter(Boolean) ?? []
    if (negatives.length)
      lines.push(`Negative prompt: ${negatives.join(', ')}`)

    if (doc.params.ar)
      lines.push(`Aspect ratio: ${doc.params.ar}`)

    return lines.join('\n')
  },
}
